import {
  AlertTriangle,
  Boxes,
  Database,
  Info,
  Loader2,
  type LucideIcon,
  Search,
  ShieldCheck,
  Sparkles,
  Store,
  Trash2,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";

import { EmptyState } from "@/components/empty-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { installPack, uninstallPack } from "@/features/marketplace/marketplace-api";
import { useT } from "@/lib/i18n";
import type { TranslationKey } from "@/lib/i18n-dict";
import type { MarketplaceItem, MarketplaceKind, MarketplaceRisk } from "@/lib/types";
import { cn } from "@/lib/utils";

interface MarketplaceBrowserProps {
  initialCatalog: MarketplaceItem[];
  initialInstalledIds: string[];
}

/**
 * `/marketplace` content. Two tabs (datapack / agents) over a single
 * flat catalog — the tab is just a `kind` filter on the mock list.
 *
 * Marketplace scope (per ADR-0001):
 *   - **Datapacks**: curated KBs the platform provides.
 *   - **Agent templates**: ready-made agent configs.
 *
 * Skills and MCP plugins are NOT here — they have dedicated surfaces
 * (`/skills`, `/mcp`). If a marketplace item depends on a skill/MCP,
 * the install flow will auto-enable it.
 *
 * Install state is browser-local until the marketplace backend ships;
 * the swap point lives in `marketplace-catalog-mock.ts`.
 */
export function MarketplaceBrowser({
  initialCatalog,
  initialInstalledIds,
}: MarketplaceBrowserProps) {
  const t = useT();
  const [installedIds, setInstalledIds] = useState<Set<string>>(() => new Set(initialInstalledIds));
  const [pending, setPending] = useState<Record<string, "install" | "uninstall" | undefined>>({});
  const [queries, setQueries] = useState<Record<MarketplaceKind, string>>({
    datapack: "",
    agents: "",
  });

  const setBusy = useCallback((itemId: string, op: "install" | "uninstall" | undefined) => {
    setPending((prev) => {
      if (op === undefined) {
        const { [itemId]: _drop, ...rest } = prev;
        return rest;
      }
      return { ...prev, [itemId]: op };
    });
  }, []);

  const handleInstall = useCallback(
    async (item: MarketplaceItem) => {
      if (pending[item.id]) return;
      setBusy(item.id, "install");
      try {
        const result = await installPack(item.id);
        const next = new Set(installedIds);
        next.add(item.id);
        setInstalledIds(next);
        const skillMsg =
          result.auto_installed_skills.length > 0
            ? ` (自动启用 skill: ${result.auto_installed_skills.join(", ")})`
            : "";
        toast.success(t("marketplace.toast.installed", { name: item.name }) + skillMsg);
      } catch (e) {
        const msg = e instanceof Error ? e.message : "安装失败";
        toast.error(msg);
      } finally {
        setBusy(item.id, undefined);
      }
    },
    [installedIds, pending, setBusy, t],
  );

  const handleUninstall = useCallback(
    async (item: MarketplaceItem) => {
      if (pending[item.id]) return;
      if (!confirm(t("marketplace.uninstall.confirm", { name: item.name }))) return;
      setBusy(item.id, "uninstall");
      try {
        await uninstallPack(item.id);
        const next = new Set(installedIds);
        next.delete(item.id);
        setInstalledIds(next);
        toast.success(t("marketplace.toast.uninstalled", { name: item.name }));
      } catch (e) {
        const msg = e instanceof Error ? e.message : "卸载失败";
        toast.error(msg);
      } finally {
        setBusy(item.id, undefined);
      }
    },
    [installedIds, pending, setBusy, t],
  );

  const tabs: TabSpec[] = useMemo(
    () => [
      {
        kind: "datapack",
        labelKey: "marketplace.tab.datapack",
        descKey: "marketplace.section.datapack.desc",
        icon: Database,
      },
      {
        kind: "agents",
        labelKey: "marketplace.tab.agents",
        descKey: "marketplace.section.agents.desc",
        icon: Boxes,
      },
    ],
    [],
  );

  return (
    <Tabs defaultValue="datapack" className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <TabsList>
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const count = countForKind(initialCatalog, tab.kind, installedIds);
            return (
              <TabsTrigger key={tab.kind} value={tab.kind} className="gap-1.5">
                <Icon className="h-3.5 w-3.5" />
                {t(tab.labelKey)}
                {count.installed > 0 ? (
                  <Badge variant="outline" className="ml-1 h-4 px-1.5 text-[9px]">
                    {count.installed}/{count.total}
                  </Badge>
                ) : null}
              </TabsTrigger>
            );
          })}
        </TabsList>
        <NoticeBadge />
      </div>

      {tabs.map((tab) => (
        <TabsContent key={tab.kind} value={tab.kind} className="mt-0 space-y-3">
          <TabPanel
            tab={tab}
            items={initialCatalog.filter((i) => i.kind === tab.kind)}
            query={queries[tab.kind]}
            onQueryChange={(next) => setQueries((prev) => ({ ...prev, [tab.kind]: next }))}
            installedIds={installedIds}
            pending={pending}
            onInstall={handleInstall}
            onUninstall={handleUninstall}
          />
        </TabsContent>
      ))}
    </Tabs>
  );
}

interface TabSpec {
  kind: MarketplaceKind;
  labelKey: TranslationKey;
  descKey: TranslationKey;
  icon: LucideIcon;
}

function NoticeBadge() {
  const t = useT();
  return (
    <div className="hidden items-start gap-1.5 rounded-md border border-dashed border-border/60 bg-muted/30 px-2 py-1 text-[10px] leading-tight text-muted-foreground md:flex">
      <Info className="mt-[1px] h-3 w-3 shrink-0" />
      <span className="max-w-[28ch]">{t("marketplace.backend.notice")}</span>
    </div>
  );
}

interface TabPanelProps {
  tab: TabSpec;
  items: MarketplaceItem[];
  query: string;
  onQueryChange: (next: string) => void;
  installedIds: Set<string>;
  pending: Record<string, "install" | "uninstall" | undefined>;
  onInstall: (item: MarketplaceItem) => void | Promise<void>;
  onUninstall: (item: MarketplaceItem) => void | Promise<void>;
}

function TabPanel({
  tab,
  items,
  query,
  onQueryChange,
  installedIds,
  pending,
  onInstall,
  onUninstall,
}: TabPanelProps) {
  const t = useT();

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => {
      return (
        item.name.toLowerCase().includes(q) ||
        item.tagline.toLowerCase().includes(q) ||
        item.description.toLowerCase().includes(q) ||
        item.bundles.some((b) => b.toLowerCase().includes(q)) ||
        item.category.toLowerCase().includes(q)
      );
    });
  }, [items, query]);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription>{t(tab.descKey)}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative max-w-sm flex-1">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="search"
              placeholder={t("marketplace.search")}
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              className="h-7 pl-7 text-[12px]"
            />
          </div>
        </div>

        {filtered.length === 0 ? (
          <EmptyState>{t("marketplace.section.empty")}</EmptyState>
        ) : (
          <div className="grid gap-2 md:grid-cols-2">
            {filtered.map((item) => (
              <MarketplaceCard
                key={item.id}
                item={item}
                installed={installedIds.has(item.id)}
                busy={pending[item.id]}
                onInstall={onInstall}
                onUninstall={onUninstall}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface MarketplaceCardProps {
  item: MarketplaceItem;
  installed: boolean;
  busy?: "install" | "uninstall";
  onInstall: (item: MarketplaceItem) => void | Promise<void>;
  onUninstall: (item: MarketplaceItem) => void | Promise<void>;
}

function MarketplaceCard({ item, installed, busy, onInstall, onUninstall }: MarketplaceCardProps) {
  const t = useT();
  const isBusy = busy !== undefined;
  const KindIcon = ICON_FOR_KIND[item.kind];

  const handleClick = () => {
    if (isBusy) return;
    if (installed) {
      void onUninstall(item);
    } else {
      void onInstall(item);
    }
  };

  let label: string;
  if (busy === "install") label = t("marketplace.installing");
  else if (busy === "uninstall") label = t("marketplace.uninstalling");
  else if (installed) label = t("marketplace.uninstall");
  else label = t("marketplace.install");

  return (
    <div className="flex flex-col gap-2 rounded-md border border-border/70 bg-card/40 p-3">
      <div className="flex items-start gap-2">
        <div className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
          <KindIcon className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1 space-y-0.5">
          <div className="flex flex-wrap items-center gap-1.5">
            <h3 className="text-[13px] font-semibold text-foreground">{item.name}</h3>
            <RiskBadge risk={item.risk} />
            {installed ? (
              <Badge
                variant="outline"
                className="h-4 px-1.5 text-[9px] uppercase tracking-[0.1em] text-emerald-600 dark:text-emerald-400"
              >
                {t("marketplace.installed")}
              </Badge>
            ) : null}
          </div>
          <p className="text-[11px] leading-relaxed text-muted-foreground">{item.tagline}</p>
        </div>
      </div>

      <p className="text-[11px] leading-relaxed text-muted-foreground/85">{item.description}</p>

      {item.bundles.length > 0 ? (
        <div className="space-y-1">
          <p className="text-[10px] font-medium uppercase tracking-[0.1em] text-muted-foreground/70">
            {t("marketplace.bundles")}
          </p>
          <div className="flex flex-wrap items-center gap-1">
            {item.bundles.map((b) => (
              <span
                key={b}
                className="rounded-sm bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground"
              >
                {b}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {item.kb_slug ? (
        <p className="text-[10px] text-muted-foreground/80">
          <Sparkles className="mr-1 inline h-2.5 w-2.5 -translate-y-px text-primary/60" />
          {t("marketplace.kb.note", { slug: item.kb_slug })}
        </p>
      ) : null}

      <div className="mt-1 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
          <span>v{item.version}</span>
          {item.vendor ? (
            <>
              <span aria-hidden>·</span>
              <span>{t("marketplace.vendor", { vendor: item.vendor })}</span>
            </>
          ) : null}
        </div>
        <Button
          type="button"
          variant={installed ? "outline" : "default"}
          size="sm"
          onClick={handleClick}
          disabled={isBusy}
          className="h-7 px-2 text-[11px]"
        >
          {isBusy ? (
            <Loader2 className="mr-1 h-3 w-3 animate-spin" />
          ) : installed ? (
            <Trash2 className="mr-1 h-3 w-3" />
          ) : null}
          {label}
        </Button>
      </div>
    </div>
  );
}

function RiskBadge({ risk }: { risk: MarketplaceRisk }) {
  if (risk === "safe") {
    return (
      <Badge
        variant="outline"
        className="h-4 gap-0.5 px-1.5 text-[9px] uppercase tracking-[0.1em] text-emerald-600 dark:text-emerald-400"
      >
        <ShieldCheck className="h-2.5 w-2.5" />
        safe
      </Badge>
    );
  }
  if (risk === "caution") {
    return (
      <Badge
        variant="outline"
        className={cn(
          "h-4 gap-0.5 px-1.5 text-[9px] uppercase tracking-[0.1em]",
          "text-amber-600 dark:text-amber-400",
        )}
      >
        <AlertTriangle className="h-2.5 w-2.5" />
        caution
      </Badge>
    );
  }
  return (
    <Badge
      variant="outline"
      className="h-4 gap-0.5 px-1.5 text-[9px] uppercase tracking-[0.1em] text-destructive"
    >
      <AlertTriangle className="h-2.5 w-2.5" />
      destructive
    </Badge>
  );
}

const ICON_FOR_KIND: Record<MarketplaceKind, LucideIcon> = {
  datapack: Database,
  agents: Boxes,
};

function countForKind(
  catalog: MarketplaceItem[],
  kind: MarketplaceKind,
  installedIds: Set<string>,
): { total: number; installed: number } {
  let total = 0;
  let installed = 0;
  for (const item of catalog) {
    if (item.kind !== kind) continue;
    total += 1;
    if (installedIds.has(item.id)) installed += 1;
  }
  return { total, installed };
}

// Surfacing `Store` so the route header strip can mirror the sidebar
// icon if needed. Re-exported here so feature consumers don't pull
// straight from `lucide-react` and end up with a different icon than
// the nav.
export { Store as MarketplaceIcon };
